import { useState, useEffect } from "react";
import styles from "../floorplans.module.scss";
import headerImage from "../../../assets/images/fp-header.jpg";

const CACHE_KEY = 'floorplan_header_data';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Preload the API endpoint
const preloadApi = () => {
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = process.env.REACT_APP_API_URL;
  document.head.appendChild(link);
};

export default function FloorplanHeader() {
  const [pageData, setPageData] = useState({
    title: "",
    description: "",
    image: "",
  });
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    // Preload API connection
    preloadApi();

    const fetchPageSettings = async () => {
      try {
        // Check cache first
        const cachedData = localStorage.getItem(CACHE_KEY);
        let shouldUseCache = false;
        
        if (cachedData) {
          const { data: cachedPageData, timestamp } = JSON.parse(cachedData);
          const isExpired = Date.now() - timestamp > CACHE_EXPIRY;
          
          if (!isExpired) {
            shouldUseCache = true;
            // Set data immediately
            setPageData(prev => ({
              ...prev,
              title: cachedPageData.floorplan?.title,
              description: cachedPageData.floorplan?.description,
              image: cachedPageData.floorplan?.image_url,
            }));
            if (cachedPageData.floorplan?.image_url) {
              loadImage(cachedPageData.floorplan.image_url);
            }
          }
        }

        // Fetch fresh data in parallel
        const fetchPromise = fetch(
          `${process.env.REACT_APP_API_URL}/wp-json/entrata/v1/getPageSettings`
        ).then(response => response.json());

        if (!shouldUseCache) {
          const newData = await fetchPromise;
          const imageUrl = newData.floorplan?.image_url;

          // Set data immediately
          setPageData(prev => ({
            ...prev,
            title: newData.floorplan?.title,
            description: newData.floorplan?.description,
            image: imageUrl,
          }));

          if (imageUrl) {
            loadImage(imageUrl);
          }

          // Cache the new data
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: newData,
            timestamp: Date.now()
          }));
        } else {
          // Still fetch fresh data to check for updates
          const newData = await fetchPromise;
          const { data: cachedPageData } = JSON.parse(cachedData);
          const hasDataChanged = 
            cachedPageData.floorplan?.title !== newData.floorplan?.title ||
            cachedPageData.floorplan?.description !== newData.floorplan?.description ||
            cachedPageData.floorplan?.image_url !== newData.floorplan?.image_url;

          if (hasDataChanged) {
            localStorage.removeItem(CACHE_KEY);
            setPageData(prev => ({
              ...prev,
              title: newData.floorplan?.title,
              description: newData.floorplan?.description,
              image: newData.floorplan?.image_url,
            }));
            if (newData.floorplan?.image_url) {
              loadImage(newData.floorplan.image_url);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching page settings:', error);
      }
    };

    const loadImage = (imageUrl) => {
      // Preload the image
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = imageUrl;
      document.head.appendChild(link);

      const img = new Image();
      img.src = imageUrl;
      
      img.onload = () => {
        setIsImageLoaded(true);
      };

      img.onerror = () => {
        console.error('Error loading image:', imageUrl);
        setIsImageLoaded(true);
      };
    };

    fetchPageSettings();
  }, []);

  return (
    <div className={styles.floorplansHeader}>
      <div className={styles.floorplansHeaderContent}>
        <h1>{pageData.title}</h1>
        <p>{pageData.description}</p>
      </div>
      {pageData.image && (
        <img
          className={`${styles.floorplansHeaderImage}`}
          src={pageData.image}
          alt="floor plan banner"
          style={{ opacity: isImageLoaded ? 1 : 0 }}
        />
      )}
    </div>
  );
}
